import React, { useState, Fragment } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faMoneyBillWave,
    faCheckCircle,
    faTimesCircle,
    faTruck,
    faCreditCard,
} from "@fortawesome/free-solid-svg-icons";
import { toast } from "react-toastify";
import api from "../../../api/api";

const DeliveryPaymentMethodSelection = ({ delivery, isOpen, onCancel, asDiv = false, onSave }) => {
    const [selectedMethod, setSelectedMethod] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [inlinePaymentLink, setInlinePaymentLink] = useState(null);

    const paymentMethods = [
        { label: "Cash on Delivery", key: "Cash on Delivery", icon: faMoneyBillWave },
        { label: "Credit/Debit Card", key: "MamoPay", icon: faCreditCard },
    ];

    const closeModal = () => {
        setSelectedMethod("");
        setInlinePaymentLink(null);
        onCancel();
    };

    const handleConfirm = async () => {
        if (!selectedMethod) {
            toast.error("Please select a payment method.");
            return;
        }
        setIsSubmitting(true);
        try {
            const response = await api.post(`/deliveries/${delivery.id}`, {
                _method: "PUT",
                updatePaymentMethod: 1,
                payment_method: selectedMethod,
            });

            if (selectedMethod !== "MamoPay") {
                toast.success("Payment method updated successfully. Your delivery will now be assigned to a driver.");
            }

            if (selectedMethod === "MamoPay" && response.data?.mamopay_payment_link) {
                setInlinePaymentLink(response.data.mamopay_payment_link);
            } else {
                closeModal();
                onSave()
            }
        } catch (error) {
            // toast.error("Failed to update payment method. Please try again.");
        } finally {
            setIsSubmitting(false);
        }
    };

    /*** Conditional Wrapper ***/
    const Wrapper = asDiv ? "div" : Transition;
    const wrapperProps = asDiv ? {} : { appear: true, show: isOpen, as: Fragment };

    return (
        <Wrapper {...wrapperProps}>
            {asDiv ? (
                <div className="w-full max-w-7xl rounded-xl bg-bodyMain px-2 sm:px-4">
                    <PaymentContent
                        selectedMethod={selectedMethod}
                        setSelectedMethod={setSelectedMethod}
                        handleConfirm={handleConfirm}
                        isSubmitting={isSubmitting}
                        inlinePaymentLink={inlinePaymentLink}
                        closeModal={closeModal}
                        paymentMethods={paymentMethods}
                        type="delivery"
                    />
                </div>
            ) : (
                <Dialog as="div" className="relative z-50" onClose={closeModal}>
                    <div className="fixed inset-0 bg-black/50 backdrop-blur-md" />
                    <div className="fixed inset-0 flex items-center justify-center p-3 sm:p-6">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 scale-95"
                            enterTo="opacity-100 scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 scale-100"
                            leaveTo="opacity-0 scale-95"
                        >
                            <Dialog.Panel className="w-full max-w-7xl rounded-xl bg-bodyMain p-4 sm:p-8 shadow-2xl">
                                <PaymentContent
                                    selectedMethod={selectedMethod}
                                    setSelectedMethod={setSelectedMethod}
                                    handleConfirm={handleConfirm}
                                    isSubmitting={isSubmitting}
                                    inlinePaymentLink={inlinePaymentLink}
                                    closeModal={closeModal}
                                    paymentMethods={paymentMethods}
                                    type="delivery"
                                />
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </Dialog>
            )}
        </Wrapper>
    );
};

/*** Extracted Payment Content (for reusability) ***/
const PaymentContent = ({ selectedMethod, setSelectedMethod, handleConfirm, isSubmitting, inlinePaymentLink, closeModal, paymentMethods, type }) => (
    <>
        <div className="flex justify-between items-center mb-4 sm:mb-6">
            <h3 className="text-xl sm:text-3xl font-bold text-purple-700 text-center w-full">
                Secure Your {type === "delivery" ? "Taxi" : "Pet's Walk"} with a Payment 🚚💳
            </h3>
            <button
                onClick={closeModal}
                className="text-gray-400 hover:text-gray-600 absolute right-3 sm:right-6 top-3 sm:top-6"
            >
                <FontAwesomeIcon icon={faTimesCircle} className="w-4 h-4 sm:w-6 sm:h-6" />
            </button>
        </div>

        {!inlinePaymentLink ? (
            <div className="space-y-4 sm:space-y-6">
                <p className="text-gray-600 text-center text-base sm:text-lg">
                    Choose a payment method to proceed with booking your {type === "delivery" ? "taxi service" : "pet's adventure"}! 📦🐾
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-6 max-w-4xl mx-auto">
                    {paymentMethods.map((method) => (
                        <button
                            type='button'
                            key={method.label}
                            onClick={() => setSelectedMethod(method.key)}
                            className={`flex flex-col items-center justify-center p-4 sm:p-6 rounded-lg shadow-lg transition-all ${selectedMethod === method.key
                                    ? "bg-primary text-white"
                                    : "bg-purple-100 text-gray-800 hover:bg-purple-200"
                                }`}
                        >
                            <FontAwesomeIcon icon={method.icon} className="text-3xl sm:text-5xl mb-2 sm:mb-3" />
                            <span className="text-base sm:text-xl font-semibold">{method.label}</span>
                            {selectedMethod === method.key && (
                                <FontAwesomeIcon
                                    icon={faCheckCircle}
                                    className="text-white text-xl sm:text-2xl mt-2 sm:mt-3"
                                />
                            )}
                        </button>
                    ))}
                </div>

                <div className="bg-yellow-100 border-l-4 border-yellow-500 p-3 sm:p-4 rounded-md flex items-start gap-2 sm:gap-3 text-gray-700 text-sm sm:text-lg">
                    <FontAwesomeIcon
                        icon={faTruck}
                        className="text-yellow-600 text-xl sm:text-2xl mt-1 flex-shrink-0"
                    />
                    <span>
                        <strong>Important Notice:</strong> To proceed further, please select a payment method.
                        We accept <strong>MamoPay</strong> for quick digital payments and <strong>Cash on Delivery</strong> if you prefer to pay later.
                    </span>
                </div>

                <div className="flex justify-center space-x-3 sm:space-x-6 mt-4 sm:mt-6">
                    <button
                        onClick={closeModal}
                        className="px-4 sm:px-6 py-2 sm:py-3 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 text-base sm:text-lg"
                        disabled={isSubmitting}
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleConfirm}
                        disabled={!selectedMethod || isSubmitting}
                        className={`px-4 sm:px-6 py-2 sm:py-3 rounded-lg text-white text-base sm:text-lg ${isSubmitting ? "bg-gray-400" : "bg-purple-600 hover:bg-purple-700"
                            }`}
                    >
                        {isSubmitting ? "Processing..." : "Confirm & Proceed"}
                    </button>
                </div>
            </div>
        ) : (
            <div className="mt-4 sm:mt-6">
                <h4 className="text-xl sm:text-2xl font-semibold text-gray-800 mb-3 sm:mb-4 text-center">
                    Complete Your Payment and Get Ready for a Fast Delivery! 🚀💳
                </h4>
                <iframe
                    src={inlinePaymentLink}
                    title="MamoPay Payment"
                    width="100%"
                    height="400vh"
                    className="rounded-lg shadow-md"
                />
            </div>
        )}
    </>
);

export default DeliveryPaymentMethodSelection;
